/**
 * Central registry for policy/sub-policy UI parity between the Policies screen
 * and the Alerts "Edit Policy" dialog.
 *
 * Any (policyKey, subPolicyKey) pair that renders `PolicyPatternModal` on the
 * Policies screen must also render it in EditPolicyDialog — and vice versa for
 * the description copy and the `Beta` chip. Historically these decisions were
 * duplicated across `ArraySettingField` + `PolicySettingField` + inline in the
 * dialog; drift was easy to introduce when a new pattern sub-policy landed.
 *
 * Keep this file as the single source of truth. Adding a new pattern-modal
 * sub-policy is a one-line change here; both surfaces pick it up automatically.
 */

/**
 * (policyKey, subPolicyKey) pairs that render `PolicyPatternModal` (regex
 * edit drawer, New/Edited/Deleted chips) instead of the inline table.
 */
const PATTERN_MODAL_SUB_POLICIES: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  'cursor-database': new Set(['maliciousPatterns', 'commandPatterns']),
  'mcp-security': new Set(['suspiciousPatterns']),
  'secret-detection': new Set(['customPatterns', 'secretFilePatterns']),
  'pii-detection': new Set(['customPatterns']),
  'extension-security': new Set(['untrustedPublishers']),
};

/**
 * Whether the given sub-policy should render `PolicyPatternModal`.
 */
export function shouldUsePatternModal(
  policyKey: string,
  subPolicyKey: string
): boolean {
  return PATTERN_MODAL_SUB_POLICIES[policyKey]?.has(subPolicyKey) ?? false;
}

/**
 * Description copy shown at the top of the pattern modal. Falls back to the
 * setting's own description when no override is registered.
 */
export function getPatternModalDescription(
  policyKey: string,
  subPolicyKey: string,
  fallback: string
): string {
  const key = `${policyKey}.${subPolicyKey}`;
  switch (key) {
    case 'cursor-database.maliciousPatterns':
      return 'Rules for detecting security risks in AI-generated source code (eval, exec, SQL injection, etc.). View the list and adjust as needed:';
    case 'cursor-database.commandPatterns':
      return 'Rules for detecting dangerous CLI commands (rm -rf, curl|sh, reverse shells, etc.). View the list and adjust as needed:';
    case 'mcp-security.suspiciousPatterns':
      return 'Define rules to detect suspicious tool descriptions. View the list and adjust as needed:';
    case 'secret-detection.customPatterns':
      return 'Rules for detecting secrets. View the list and adjust as needed:';
    case 'secret-detection.secretFilePatterns':
      return 'Detect secret file rules. View the list and adjust as needed:';
    case 'pii-detection.customPatterns':
      return 'Custom regex patterns detected as DLP custom infoTypes (in addition to the built-in PII types). View the list and adjust as needed:';
    case 'extension-security.untrustedPublishers':
      return 'Define patterns for untrusted publisher names. View the list and adjust the patterns as needed:';
    default:
      return fallback;
  }
}

/**
 * (policyKey, subPolicyKey) pairs that carry the `Beta` chip on both the
 * Policies screen and the Alerts EditPolicyDialog.
 */
const BETA_SUB_POLICIES: Readonly<Record<string, ReadonlySet<string>>> = {
  'extension-security': new Set(['extensionsCodeScan']),
};

/**
 * Whether the given sub-policy should render `BetaBadge`.
 */
export function hasBetaBadge(policyKey: string, subPolicyKey: string): boolean {
  return BETA_SUB_POLICIES[policyKey]?.has(subPolicyKey) ?? false;
}
